import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Hammer, Layers, Home, Grid, Phone, Mail, ArrowRight, Check, Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { CONTACT_INFO, IMAGES, SERVICES, REVIEWS, FALLBACK_PROJECTS } from "../data";
import ContactForm from "../components/ContactForm";
import Meta from "../components/Meta";
import { motion } from "framer-motion";
import {
  fadeIn,
  fadeInSlow,
  fadeInSimple,
  slideInLeft,
  slideInRight,
  staggerContainer,
  staggerItem,
  heroText,
  heroSubText,
  heroButtons
} from "../lib/animations";

export default function Forside() {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(3);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/content/referencer");
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setProjects(data);
            return;
          }
        }
        setProjects(FALLBACK_PROJECTS);
      } catch (error) {
        console.warn("Kunne ikke hente projekter fra CMS, bruger lokale data:", error);
        setProjects(FALLBACK_PROJECTS);
      } finally {
        setLoadingProjects(false);
      }
    }
    fetchProjects();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleReviewsCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleReviewsCount(2);
      } else {
        setVisibleReviewsCount(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxReviewIndex = Math.max(0, REVIEWS.length - visibleReviewsCount);

  useEffect(() => {
    if (currentReviewIndex > maxReviewIndex) {
      setCurrentReviewIndex(maxReviewIndex);
    }
  }, [visibleReviewsCount, maxReviewIndex, currentReviewIndex]);

  const nextReview = () => {
    setCurrentReviewIndex((prev) => (prev >= maxReviewIndex ? 0 : prev + 1));
  };

  const prevReview = () => {
    setCurrentReviewIndex((prev) => (prev <= 0 ? maxReviewIndex : prev - 1));
  };

  // Map icon strings to Lucide components
  const getIcon = (iconName) => {
    switch (iconName) {
      case "Hammer":
        return <Hammer className="w-8 h-8 text-amber-500" />;
      case "Layers":
        return <Layers className="w-8 h-8 text-amber-500" />;
      case "Home":
        return <Home className="w-8 h-8 text-amber-500" />;
      case "Grid":
        return <Grid className="w-8 h-8 text-amber-500" />;
      default:
        return <Hammer className="w-8 h-8 text-amber-500" />;
    }
  };

  // Map category to service page route
  const getCategoryLink = (category) => {
    switch (category) {
      case "Tømrerarbejde":
        return "/toemrerarbejde";
      case "Terrasse":
        return "/terrasse";
      case "Tagarbejde":
        return "/tagarbejde";
      case "Murer & Belægning":
        return "/murer-belaegning";
      default:
        return "/toemrerarbejde";
    }
  };

  return (
    <>
      <Meta
        title="Forside"
        description="Nordsjællands Tømrer og Snedker udfører professionelt tømrerarbejde, terrasser og tagarbejde i Helsingør og Nordsjælland. Kontakt os for solidt håndværk."
      />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-zinc-950 text-white overflow-hidden rounded-none">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Moderne dansk bolig med tømrerarbejde"
            className="w-full h-full object-cover opacity-60 scale-105 animate-subtleZoom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/30 to-zinc-950/40" />
        </div>

        {/* Content */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="relative z-10 max-w-5xl w-full mx-auto px-6 md:px-12 text-left pt-20"
        >
          <motion.span
            variants={heroText}
            className="inline-block text-xs font-bold uppercase tracking-[0.3em] text-amber-300 mb-4"
          >
            Mere end 20 års erfaring • Tømrer- & snedkerarbejde i Nordsjælland
          </motion.span>
          <motion.h1
            variants={heroText}
            className="text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-stone-50 uppercase leading-tight"
          >
            Solidt håndværk <br />
            <span className="font-semibold text-amber-500">tilpasset din bolig</span>
          </motion.h1>
          <motion.p
            variants={heroSubText}
            className="mt-6 text-base sm:text-lg text-stone-300 max-w-2xl font-light leading-relaxed"
          >
            Nordsjællands Tømrer og Snedker leverer kompromisløs kvalitet inden for store tilbygninger, ombygninger, tagrenovering og specialdesignede træterrasser. Vi sikrer din bolig med personlig rådgivning og trygge løsninger.
          </motion.p>
          <motion.div
            variants={heroButtons}
            className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4"
          >
            <Link
              to="/kontakt"
              className="w-full sm:w-auto px-8 py-4 bg-amber-800 hover:bg-amber-700 text-stone-50 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none flex items-center justify-center gap-2"
            >
              <span>Få et tilbud</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/toemrerarbejde"
              className="w-full sm:w-auto px-8 py-4 border border-stone-400 hover:border-amber-500 hover:text-amber-500 text-stone-200 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none flex items-center justify-center"
            >
              Vores ydelser
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* About & Detail Intro Section */}
      <section className="bg-stone-50 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column (Copy) */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
              Lokal tømrer i Helsingør & Nordsjælland
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wide text-zinc-900 leading-tight">
              Vi bygger på tillid, kvalitet og godt håndværk
            </h2>
            <p className="text-stone-600 leading-relaxed font-light">
              Hos <strong>Nordsjællands Tømrer og Snedker</strong> forstår vi, at din bolig er din største investering. Derfor går vi aldrig på kompromis med kvaliteten af vores arbejde. Med mere end 20 års erfaring og base i Helsingør dækker vi hele Nordsjælland og udfører alt fra mindre snedkeropgaver til store tilbygninger, ombygninger, tagrenoveringer og komplette træterrasser.
            </p>
            <p className="text-stone-600 leading-relaxed font-light">
              Vi lægger stor vægt på den personlige dialog. For os er det afgørende, at du føler dig tryg gennem hele byggeprocessen – lige fra den første rådgivning til det færdige resultat står skarpt og klar til brug.
            </p>
            <div className="pt-4">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-900 hover:text-amber-700 transition-colors duration-200 border-b-2 border-zinc-900 hover:border-amber-700 pb-1"
              >
                <span>Læs mere om os</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Right Column (Visual) */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="relative h-[450px] overflow-hidden rounded-none shadow-none border border-stone-200"
          >
            <img
              src={IMAGES.omos}
              alt="Professionel tømrer i Helsingør"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-zinc-900 py-24 px-6 md:px-12 text-white rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeIn}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center max-w-3xl mx-auto mb-16 space-y-4"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
              Vores ekspertiseområder
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-stone-50">
              Hvad kan vi hjælpe dig med?
            </h2>
            <p className="text-stone-400 font-light leading-relaxed">
              Vi tilbyder et bredt udvalg af tømrer- og snedkerløsninger med fokus på holdbarhed og æstetik. Se vores primære arbejdsområder herunder.
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.id}
                variants={staggerItem}
                className="bg-zinc-950 border border-zinc-800/80 p-8 flex flex-col justify-between hover:border-amber-500/50 transition-all duration-300 rounded-none group"
              >
                <div className="space-y-6">
                  <div className="p-3 bg-zinc-900 inline-block rounded-none border border-zinc-800">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-wide text-stone-100">
                    {service.title}
                  </h3>
                  <p className="text-stone-400 text-sm leading-relaxed font-light">
                    {service.shortDesc}
                  </p>
                  <ul className="space-y-2 pt-2">
                    {service.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-center gap-2.5 text-xs text-stone-300">
                        <Check className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-8">
                  <Link
                    to={service.path}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-500 group-hover:text-amber-400 transition-colors duration-200"
                  >
                    <span>Læs mere</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Split-Feature Architectural Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 w-full rounded-none overflow-hidden border-y border-zinc-800">
        {/* Column 1: Services List - Light Solid */}
        <motion.div
          variants={fadeIn}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-50px" }}
          className="bg-stone-100 p-12 flex flex-col justify-center text-zinc-900 min-h-[480px]"
        >
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-800">
              Kvalitetsgaranti
            </span>
            <h3 className="text-xl font-bold uppercase tracking-wider text-zinc-900 mt-2 mb-6">
              Vores faglige stolthed
            </h3>
            <ul className="space-y-3 mb-8">
              {[
                "Uddannede tømrere & snedkere",
                "Lokalt kendskab i Nordsjælland",
                "Brug af certificerede materialer",
                "Gennemskuelige tilbud uden skjulte gebyrer",
                "Oprydning efter endt arbejde"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs font-medium text-stone-700">
                  <Check className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="pt-6 border-t border-stone-200 flex items-center gap-4">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/bf156273-6242-48dd-a9a0-7699f46947d9/ambassadoer-2026-bagde-lille.png" 
                alt="3byggetilbud.dk Ambassadør 2026" 
                className="h-12 w-auto object-contain bg-white p-1 border border-stone-200"
              />
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-900 block">3byggetilbud.dk</span>
                <span className="text-[11px] text-stone-600 font-light block">Certificeret Ambassadør</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Column 2: Project Image Showcase */}
        <motion.div
          variants={fadeInSlow}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-50px" }}
          className="h-[480px] lg:h-auto relative"
        >
          <img
            src={IMAGES.toemrer}
            alt="Tømrerarbejde i høj kvalitet"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Column 3: Call-to-Action - Dark Solid */}
        <motion.div
          variants={fadeIn}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-50px" }}
          className="bg-amber-500 text-stone-50 p-12 flex flex-col justify-center items-start text-left min-h-[480px] space-y-8"
        >
          <div className="space-y-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white">
              Uforpligtende tilbud
            </span>
            <h3 className="text-xl font-bold uppercase tracking-wider text-stone-50">
              Skal vi kigge på dit projekt?
            </h3>
            <p className="text-xs text-stone-300 leading-relaxed font-light max-w-xs">
              Kontakt os i dag for en uforpligtende snak om dine drømme. Vi kommer meget gerne ud og besigtiger din opgave.
            </p>
          </div>
          <div className="w-full max-w-xs pt-2">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="w-full py-3 bg-stone-50 hover:bg-stone-200 text-zinc-950 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>Ring {CONTACT_INFO.phoneFormatted}</span>
            </a>
          </div>
        </motion.div>

        {/* Column 4: Interior Image Showcase */}
        <motion.div
          variants={fadeInSlow}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-50px" }}
          className="h-[480px] lg:h-auto relative"
        >
          <img
            src={IMAGES.terrasse}
            alt="Smuk træterrasse"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </section>

      {/* Recent Projects Section (CMS) */}
      <section className="bg-zinc-950 py-24 px-6 md:px-12 text-white rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeIn}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6"
          >
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
                Udført arbejde
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-stone-50">
                Vi Har Hjulpet Med
              </h2>
              <p className="text-stone-400 font-light max-w-2xl leading-relaxed">
                Her er et udvalg af vores seneste projekter udført for tilfredse kunder i Helsingør, Hornbæk, Snekkersten og resten af Nordsjælland.
              </p>
            </div>
            <div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-stone-300 text-xs font-bold uppercase tracking-widest transition-colors duration-200 rounded-none"
              >
                <span>Start dit projekt</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Projects Grid */}
          {loadingProjects ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-900 h-80 animate-pulse rounded-none" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 border border-zinc-900">
              <p className="text-stone-400 text-sm">Ingen projekter fundet i øjeblikket.</p>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {projects.map((project) => (
                <motion.div key={project.id} variants={staggerItem}>
                  <Link
                    to={getCategoryLink(project.category)}
                    className="bg-zinc-900 border border-zinc-800/60 flex flex-col rounded-none overflow-hidden group hover:border-amber-500/50 transition-all duration-300 h-full"
                  >
                    <div className="h-56 overflow-hidden relative">
                      <img
                        src={project.image?.[0] || IMAGES.hero}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3 bg-amber-600 border border-amber-500/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white rounded-none">
                        {project.category}
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col justify-center">
                      <h3 className="text-sm font-bold uppercase tracking-wide text-stone-100 group-hover:text-amber-500 transition-colors duration-200">
                        {project.title}
                      </h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-stone-100 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeIn}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center max-w-3xl mx-auto mb-16 space-y-4"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-amber-800">
              Kundernes ord
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-zinc-900">
              Hvad siger vores kunder?
            </h2>
            <p className="text-stone-600 font-light leading-relaxed">
              Troværdighed og tilfredse kunder er fundamentet for vores forretning. Her kan du læse udtalelser fra nogle af de boligejere, vi har hjulpet.
            </p>
          </motion.div>

          {/* Slider Wrapper */}
          <motion.div
            variants={fadeInSlow}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="relative px-0 sm:px-12"
          >
            {/* Carousel Viewport */}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out -mx-3"
                style={{
                  transform: `translateX(-${currentReviewIndex * (100 / visibleReviewsCount)}%)`,
                }}
              >
                {REVIEWS.map((review, idx) => (
                  <div
                    key={idx}
                    className="w-full sm:w-1/2 lg:w-1/3 shrink-0 px-3"
                  >
                    <div className="bg-white border border-stone-200 p-8 h-full flex flex-col justify-between rounded-none shadow-none relative">
                      <Quote className="absolute right-6 top-6 w-10 h-10 text-stone-200/60" />
                      <div className="space-y-4 relative z-10">
                        <div className="flex gap-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                          ))}
                        </div>
                        <p className="text-stone-600 text-sm leading-relaxed font-light italic">
                          "{review.text}"
                        </p>
                      </div>
                      <div className="pt-6 border-t border-stone-100 mt-6 flex flex-col">
                        <span className="text-xs font-bold uppercase tracking-wider text-zinc-900">
                          {review.name}
                        </span>
                        <span className="text-[10px] text-stone-500 font-semibold uppercase tracking-widest mt-0.5">
                          {review.location}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="flex justify-center sm:block mt-6 sm:mt-0">
              <button
                onClick={prevReview}
                className="mx-2 sm:mx-0 sm:absolute sm:-left-4 lg:-left-8 sm:top-1/2 sm:-translate-y-1/2 w-10 h-10 bg-white border border-stone-200 hover:border-amber-500 hover:text-amber-500 text-zinc-900 flex items-center justify-center transition-colors duration-200 rounded-none shadow-none z-10"
                aria-label="Forrige anmeldelse"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextReview}
                className="mx-2 sm:mx-0 sm:absolute sm:-right-4 lg:-right-8 sm:top-1/2 sm:-translate-y-1/2 w-10 h-10 bg-white border border-stone-200 hover:border-amber-500 hover:text-amber-500 text-zinc-900 flex items-center justify-center transition-colors duration-200 rounded-none shadow-none z-10"
                aria-label="Næste anmeldelse"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {[...Array(maxReviewIndex + 1)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentReviewIndex(idx)}
                  className={`w-2 h-2 transition-all duration-300 rounded-none ${
                    currentReviewIndex === idx
                      ? "bg-amber-500 w-6"
                      : "bg-stone-300 hover:bg-stone-400"
                  }`}
                  aria-label={`Gå til slide ${idx + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative bg-zinc-950 py-24 px-6 md:px-12 text-white rounded-none border-t border-zinc-800 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Kontakt baggrund"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column: Contact info */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-8 flex flex-col justify-between"
          >
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
                Kontakt os i dag
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-stone-50 leading-tight">
                Lad os tage en uforpligtende snak om dit projekt
              </h2>
              <p className="text-stone-400 font-light leading-relaxed">
                Uanset om du skal have lagt nyt tag, bygget en smuk træterrasse eller udført andet tømrerarbejde, står vi klar til at rådgive dig. Vi udarbejder altid et skriftligt, gennemskueligt og uforpligtende tilbud.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Ring direkte</span>
                    <a
                      href={`tel:${CONTACT_INFO.phone}`}
                      className="text-sm font-bold text-stone-100 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
                    >
                      {CONTACT_INFO.phoneFormatted}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">E-mail</span>
                    <a
                      href={`mailto:${CONTACT_INFO.email}`}
                      className="text-sm font-bold text-stone-100 hover:text-amber-500 transition-colors duration-200 break-all"
                    >
                      {CONTACT_INFO.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Home className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Adresse</span>
                    <span className="text-sm font-semibold text-stone-200">
                      {CONTACT_INFO.address}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-xs text-stone-500 border-t border-zinc-800/80 pt-6">
              Nordsjællands Tømrer og Snedker · CVR: {CONTACT_INFO.cvr} · Base i Helsingør
            </div>
          </motion.div>

          {/* Right Column: Form */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="bg-zinc-950 border border-zinc-800/80 p-8 md:p-10 rounded-none"
          >
            <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 mb-6">
              Send os en besked
            </h3>
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </>
  );
}
